/**
 * DriveZone — Login Page
 * Route: /login
 */

import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import api from '@/lib/api'
import { useAuthStore, type AuthUser } from '@/stores/authStore'
import { AxiosError } from 'axios'

interface LoginResponse {
  message: string
  temp_token?: string
  user?: AuthUser
}

export default function LoginPage() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const { setTempToken, setUser } = useAuthStore()

  const successMessage = (location.state as { message?: string })?.message

  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw]     = useState(false)
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState('')

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const res = await api.post<LoginResponse>(
        '/api/auth/login/',
        { email: email.trim().toLowerCase(), password }
      )

      if (res.data.user) {
        setUser(res.data.user)
        navigate('/app/search', { replace: true })
      } else {
        setTempToken(res.data.temp_token!)
        navigate('/verify-otp', { state: { email: email.trim().toLowerCase() } })
      }

    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string }>
      if (axiosErr.response?.status === 429) {
        setError('Too many attempts. Please wait 15 minutes before trying again.')
      } else if (axiosErr.response?.status === 401) {
        setError('Invalid email or password.')
      } else {
        setError('Something went wrong. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)' }}>
      <div className="w-full max-w-sm">

        {/* Brand */}
        <div className="text-center mb-8">
          <img src="/favicon.svg" alt="DriveZone" className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-semibold text-white">DriveZone</h1>
          <p className="text-xs font-medium tracking-widest text-sky-100 uppercase mt-2">
            Please sign in to continue
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 space-y-5">
          {successMessage && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
              <p className="text-sm text-emerald-700">{successMessage}</p>
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="relative">
              <input
                id="login-email"
                type="email"
                placeholder=" "
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
                className="peer w-full px-4 pt-6 pb-2 rounded-lg border-2 border-slate-400 bg-slate-50
                           text-slate-900 text-sm
                           focus:outline-none focus:border-sky-500 focus:bg-white
                           transition-colors duration-150"
              />
              <label
                htmlFor="login-email"
                className="absolute left-4 top-4 text-sm text-slate-500 pointer-events-none
                           transition-all duration-150 origin-left
                           peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:text-sky-600 peer-focus:font-medium
                           peer-[:not(:placeholder-shown)]:top-1.5 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:font-medium"
              >
                E-mail
              </label>
            </div>

            {/* Password */}
            <div className="relative">
              <input
                id="login-password"
                type={showPw ? 'text' : 'password'}
                placeholder=" "
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="peer w-full px-4 pt-6 pb-2 pr-12 rounded-lg border-2 border-slate-400 bg-slate-50
                           text-slate-900 text-sm
                           focus:outline-none focus:border-sky-500 focus:bg-white
                           transition-colors duration-150"
              />
              <label
                htmlFor="login-password"
                className="absolute left-4 top-4 text-sm text-slate-500 pointer-events-none
                           transition-all duration-150 origin-left
                           peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:text-sky-600 peer-focus:font-medium
                           peer-[:not(:placeholder-shown)]:top-1.5 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:font-medium"
              >
                Password
              </label>
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <div className="text-right -mt-2">
              <Link to="/forgot-password" className="text-xs font-medium text-sky-600 hover:underline">
                Forgot your password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full py-3.5 rounded-lg font-semibold text-sm text-white
                         bg-sky-600 hover:bg-sky-700 active:bg-sky-800
                         transition-colors duration-150
                         disabled:opacity-50 disabled:cursor-not-allowed
                         flex items-center justify-center gap-2"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-sky-100 mt-6">
          Don't have an account?{' '}
          <Link to="/register" className="text-white font-semibold hover:underline">
            Create one
          </Link>
        </p>
      </div>
    </div>
  )
}
